import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Search, Plus, Apple, Clock, X, CheckCircle2, Heart } from 'lucide-react';
import { Food, UserFood } from '@/hooks/useComprehensiveNutrition';
import { useComprehensiveNutrition } from '@/contexts/NutritionContext';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Analytics } from '@/lib/analytics';



interface FoodSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectFood: (food: Food | UserFood, quantity: number, unit: string) => void;
  mealType?: 'breakfast' | 'lunch' | 'dinner' | 'snack';
}

export const FoodSearchModal = ({ isOpen, onClose, onSelectFood, mealType }: FoodSearchModalProps) => {
  const { t } = useTranslation();
  const { searchFoods, userFoods, createCustomFood, getRecentFoods } = useComprehensiveNutrition();
  const { toast } = useToast();
  const mealLabel = mealType
    ? t(`components.nutritionDashboard.meals.${mealType === 'snack' ? 'snacks' : mealType}`)
    : '';
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Food[]>([]);
  const [recentFoods, setRecentFoods] = useState<any[]>([]);
  const [favoriteFoods, setFavoriteFoods] = useState<any[]>([]);
  const [favoriteIds, setFavoriteIds] = useState<Set<string>>(new Set());
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [apiStatus, setApiStatus] = useState<{
    basic: 'idle' | 'loading' | 'success' | 'error';
    local: 'idle' | 'loading' | 'success' | 'error';
    usda: 'idle' | 'loading' | 'success' | 'error';
    off: 'idle' | 'loading' | 'success' | 'error';
  }>({
    basic: 'idle',
    local: 'idle',
    usda: 'idle',
    off: 'idle'
  });
  const [selectedFood, setSelectedFood] = useState<Food | UserFood | null>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const searchAbortControllerRef = useRef<AbortController | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [unit, setUnit] = useState('g');
  const [showCreateFood, setShowCreateFood] = useState(false);
  
  const [newFood, setNewFood] = useState({
    name: '',
    serving_size: 100,
    serving_unit: 'g',
    calories_per_serving: 0,
    protein_per_serving: 0,
    carbs_per_serving: 0,
    fat_per_serving: 0,
    fiber_per_serving: 0,
    sugar_per_serving: 0,
    sodium_per_serving: 0
  });

  useEffect(() => {
    if (isOpen) {
      loadRecentFoods();
      loadFavoriteFoods();
    } else {
      // Clear all states when modal closes
      setSearchQuery('');
      setSearchResults([]);
      setSearchError(null);
      setIsSearching(false);
      setSelectedFood(null);
      setQuantity(1);
      setUnit('g');
      setShowCreateFood(false);
      setApiStatus({ basic: 'idle', local: 'idle', usda: 'idle', off: 'idle' });
      
      // Abort any ongoing search
      if (searchAbortControllerRef.current) {
        searchAbortControllerRef.current.abort();
        searchAbortControllerRef.current = null;
      }
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = null;
      }
    }
  }, [isOpen]);

  const loadRecentFoods = async () => {
    try {
      const recent = await getRecentFoods();
      setRecentFoods(recent);
    } catch (error) {
      console.error('Error loading recent foods:', error);
    }
  };

  const loadFavoriteFoods = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: favorites, error } = await supabase
        .from('favorite_foods')
        .select(`
          id,
          food_id,
          basic_food_id,
          user_food_id,
          created_at
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Create a set of favorite IDs for quick lookup
      const favIds = new Set<string>();
      const enrichedFavorites: any[] = [];

      // Fetch details for each favorite food
      for (const fav of favorites || []) {
        let foodData = null;
        
        if (fav.food_id) {
          favIds.add(`food_${fav.food_id}`);
          const { data } = await supabase
            .from('foods')
            .select('*')
            .eq('id', fav.food_id)
            .single();
          foodData = data ? { ...data, favoriteId: fav.id } : null;
        } else if (fav.basic_food_id) {
          favIds.add(`basic_${fav.basic_food_id}`);
          const { data } = await supabase
            .from('basic_foods')
            .select('*')
            .eq('id', fav.basic_food_id)
            .single();
          foodData = data ? { ...data, favoriteId: fav.id, verified: true } : null;
        } else if (fav.user_food_id) {
          favIds.add(`user_${fav.user_food_id}`);
          const { data } = await supabase
            .from('user_foods')
            .select('*')
            .eq('id', fav.user_food_id)
            .single();
          foodData = data ? { ...data, favoriteId: fav.id } : null;
        }

        if (foodData) {
          enrichedFavorites.push(foodData);
        }
      }

      setFavoriteIds(favIds);
      setFavoriteFoods(enrichedFavorites);
    } catch (error) {
      console.error('Error loading favorite foods:', error);
    }
  };

  const isExternalFood = (food: any): boolean => {
    // External foods have string IDs starting with 'off_' (OpenFoodFacts) or 'usda_' (USDA)
    return typeof food.id === 'string' && (food.id.startsWith('off_') || food.id.startsWith('usda_'));
  };

  const saveExternalFoodToDatabase = async (food: any, userId: string): Promise<string | null> => {
    // Save external food to foods table and return the new UUID
    const { data, error } = await supabase
      .from('foods')
      .insert({
        name: food.name,
        brand: food.brand || null,
        serving_size: food.serving_size || 100,
        serving_unit: food.serving_unit || 'g',
        calories_per_serving: food.calories_per_serving || 0,
        protein_per_serving: food.protein_per_serving || 0,
        carbs_per_serving: food.carbs_per_serving || 0,
        fat_per_serving: food.fat_per_serving || 0,
        fiber_per_serving: food.fiber_per_serving || 0,
        sugar_per_serving: food.sugar_per_serving || 0,
        sodium_per_serving: food.sodium_per_serving || 0,
        is_public: true,
        created_by: userId
      })
      .select('id')
      .single();

    if (error) {
      console.error('Error saving external food:', error);
      return null;
    }
    return data?.id || null;
  };

  const toggleFavorite = async (food: any) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Handle external foods (OpenFoodFacts/USDA) - they need to be saved first
      let foodToFavorite = food;
      let foodKey: string;

      if (isExternalFood(food)) {
        // Check if we already have this in favorites by name (approximate match)
        const existingFavorite = favoriteFoods.find(f => f.name === food.name);
        if (existingFavorite) {
          // Already favorited, remove it
          if (existingFavorite.favoriteId) {
            // Optimistic update - remove from UI immediately
            setFavoriteIds(prev => {
              const next = new Set(prev);
              next.delete(`food_${existingFavorite.id}`);
              next.delete(`external_${food.name}`);
              return next;
            });

            const { error } = await supabase
              .from('favorite_foods')
              .delete()
              .eq('id', existingFavorite.favoriteId);

            if (error) throw error;

            toast({
              title: t('components.foodSearch.removedFromFavorites'),
              description: t('components.foodSearch.favoriteRemovedDescription', { name: food.name })
            });
            await loadFavoriteFoods();
            return;
          }
        }

        // Optimistic update - add to UI immediately with external key
        setFavoriteIds(prev => new Set(prev).add(`external_${food.name}`));

        // Save external food to database first
        const newFoodId = await saveExternalFoodToDatabase(food, user.id);
        if (!newFoodId) {
          // Revert optimistic update
          setFavoriteIds(prev => {
            const next = new Set(prev);
            next.delete(`external_${food.name}`);
            return next;
          });
          throw new Error('Failed to save food to database');
        }
        foodToFavorite = { ...food, id: newFoodId };
        foodKey = `food_${newFoodId}`;
      } else {
        foodKey = 
          'user_id' in food ? `user_${food.id}` :
          (food as any).source === 'basic' || food.category ? `basic_${food.id}` :
          `food_${food.id}`;
      }

      const isFavorited = favoriteIds.has(foodKey);

      if (isFavorited) {
        // Optimistic update - remove from UI immediately
        setFavoriteIds(prev => {
          const next = new Set(prev);
          next.delete(foodKey);
          return next;
        });

        // Remove from favorites
        const favoriteRecord = favoriteFoods.find(f => f.id === foodToFavorite.id);
        if (favoriteRecord?.favoriteId) {
          const { error } = await supabase
            .from('favorite_foods')
            .delete()
            .eq('id', favoriteRecord.favoriteId);

          if (error) {
            // Revert optimistic update
            setFavoriteIds(prev => new Set(prev).add(foodKey));
            throw error;
          }

          toast({
            title: t('components.foodSearch.removedFromFavorites'),
            description: t('components.foodSearch.favoriteRemovedDescription', { name: food.name })
          });
        }
      } else {
        // Optimistic update - add to UI immediately
        setFavoriteIds(prev => new Set(prev).add(foodKey));

        // Add to favorites
        const insertData: any = { 
          user_id: user.id,
          food_id: null,
          basic_food_id: null,
          user_food_id: null
        };
        
        // Set only ONE of the foreign keys
        if ('user_id' in food && !isExternalFood(food)) {
          insertData.user_food_id = food.id;
        } else if ((food as any).source === 'basic' || food.category) {
          insertData.basic_food_id = food.id;
        } else {
          insertData.food_id = foodToFavorite.id;
        }

        const { error } = await supabase
          .from('favorite_foods')
          .insert(insertData);

        if (error) {
          // Revert optimistic update
          setFavoriteIds(prev => {
            const next = new Set(prev);
            next.delete(foodKey);
            return next;
          });
          throw error;
        }

        toast({
          title: t('components.foodSearch.addedToFavorites'),
          description: t('components.foodSearch.favoriteAddedDescription', { name: food.name })
        });
      }

      // Reload favorites
      await loadFavoriteFoods();
    } catch (error: any) {
      console.error('Error toggling favorite:', error);
      toast({
        title: t('components.foodSearch.errorTitle'),
        description: error.message || t('components.foodSearch.favoritesError'),
        variant: "destructive"
      });
    }
  };

  const isFavorite = (food: any): boolean => {
    // Check for external foods first (by name key)
    if (isExternalFood(food)) {
      if (favoriteIds.has(`external_${food.name}`)) return true;
      // Also check if already saved to favorites by name match
      return favoriteFoods.some(f => f.name === food.name);
    }

    const foodKey = 
      'user_id' in food ? `user_${food.id}` :
      (food as any).source === 'basic' || food.category ? `basic_${food.id}` :
      `food_${food.id}`;
    return favoriteIds.has(foodKey);
  };

  const calculateLocalRelevance = (food: Food | UserFood, query: string): number => {
    const name = food.name.toLowerCase();
    const brand = ('brand' in food && food.brand) ? food.brand.toLowerCase() : '';
    const searchQuery = query.toLowerCase();
    
    // Tokenize query into words for better matching
    const queryWords = searchQuery.split(/\s+/).filter(w => w.length > 0);
    const nameWords = name.split(/\s+/);
    const brandWords = brand.split(/\s+/);
    
    let score = 0;
    
    // EXACT MATCHES (highest priority)
    if (name === searchQuery) score += 150;
    if (brand === searchQuery) score += 140;
    
    // FULL STRING MATCHES
    if (name.startsWith(searchQuery)) score += 80;
    if (name.includes(searchQuery)) score += 40;
    if (brand.includes(searchQuery)) score += 35;
    
    // BRAND + PRODUCT COMBINATION
    const combinedText = `${name} ${brand}`;
    if (combinedText.includes(searchQuery)) score += 45;
    
    // WORD-BY-WORD MATCHING (order independent)
    let matchedWords = 0;
    for (const queryWord of queryWords) {
      const nameMatch = nameWords.some(nw => 
        nw === queryWord || nw.startsWith(queryWord) || nw.includes(queryWord)
      );
      if (nameMatch) {
        matchedWords++;
        score += 30;
      }
      
      const brandMatch = brandWords.some(bw => 
        bw === queryWord || bw.startsWith(queryWord) || bw.includes(queryWord)
      );
      if (brandMatch) {
        matchedWords++;
        score += 25;
      }
    }
    
    // Boost if all query words are found
    if (queryWords.length > 0 && matchedWords >= queryWords.length) {
      score += 50;
    }
    
    // QUALITY INDICATORS
    // Boost verified foods
    if (food.verified) score += 15;
    
    // Boost user's custom foods (they created them for a reason)
    if ('user_id' in food) score += 20;
    
    return score;
  };

  // Retry function with exponential backoff
  const retryWithBackoff = async <T,>(
    fn: () => Promise<T>,
    maxRetries: number = 3,
    baseDelay: number = 500
  ): Promise<T> => {
    let lastError: any;
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        return await fn();
      } catch (error) {
        lastError = error;
        if (attempt < maxRetries - 1) {
          const delay = baseDelay * Math.pow(2, attempt);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }
    throw lastError;
  };

  // Timeout wrapper for API calls
  const withTimeout = (promise: PromiseLike<any>, timeoutMs: number = 5000): Promise<any> => {
    return Promise.race([
      Promise.resolve(promise),
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Request timeout')), timeoutMs)
      )
    ]);
  };

  const handleSearch = useCallback(async (query: string, signal?: AbortSignal) => {
    if (!query.trim()) {
      setSearchResults([]);
      setSearchError(null);
      setApiStatus({ basic: 'idle', local: 'idle', usda: 'idle', off: 'idle' });
      setIsSearching(false);
      return;
    }

    // Check if aborted before starting
    if (signal?.aborted) {
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    setSearchError(null);
    setApiStatus({ basic: 'loading', local: 'loading', usda: 'loading', off: 'loading' });

    try {
      console.log('[FoodSearch] Searching for:', query);
      
      // Check if aborted before starting searches
      if (signal?.aborted) {
        setIsSearching(false);
        return;
      }

      // Search with Promise.allSettled to handle partial failures gracefully
      const searchPromises = [
        // Basic foods - most reliable, no retry needed
        (async () => {
          try {
            if (signal?.aborted) return { type: 'basic' as const, data: [], error: null };
            const result = await withTimeout(
              supabase.from('basic_foods').select('*').ilike('name', `%${query}%`).limit(20),
              3000
            );
            if (signal?.aborted) return { type: 'basic' as const, data: [], error: null };
            setApiStatus(prev => ({ ...prev, basic: 'success' }));
            return { type: 'basic' as const, data: result.data || [], error: result.error };
          } catch (error) {
            if (signal?.aborted) return { type: 'basic' as const, data: [], error: null };
            console.error('[FoodSearch] Basic foods error:', error);
            setApiStatus(prev => ({ ...prev, basic: 'error' }));
            return { type: 'basic' as const, data: [], error };
          }
        })(),
        
        // Local database - reliable, with retry
        (async () => {
          try {
            if (signal?.aborted) return { type: 'local' as const, data: [], error: null };
            const results = await retryWithBackoff(() => {
              if (signal?.aborted) throw new Error('Aborted');
              return searchFoods(query);
            }, 2, 300);
            if (signal?.aborted) return { type: 'local' as const, data: [], error: null };
            setApiStatus(prev => ({ ...prev, local: 'success' }));
            return { type: 'local' as const, data: results, error: null };
          } catch (error) {
            if (signal?.aborted) return { type: 'local' as const, data: [], error: null };
            console.error('[FoodSearch] Local search error:', error);
            setApiStatus(prev => ({ ...prev, local: 'error' }));
            return { type: 'local' as const, data: [], error };
          }
        })(),
        
        // USDA API - with retry and timeout
        (async () => {
          try {
            if (signal?.aborted) return { type: 'usda' as const, data: [], error: null };
            const response = await retryWithBackoff(
              () => {
                if (signal?.aborted) throw new Error('Aborted');
                return withTimeout(
                  supabase.functions.invoke('search-usda-foods', { body: { query } }),
                  8000
                );
              },
              2,
              500
            );
            if (signal?.aborted) return { type: 'usda' as const, data: [], error: null };
            setApiStatus(prev => ({ ...prev, usda: 'success' }));
            return { type: 'usda' as const, data: response.data?.foods || [], error: response.error };
          } catch (error) {
            if (signal?.aborted) return { type: 'usda' as const, data: [], error: null };
            console.error('[FoodSearch] USDA API error:', error);
            setApiStatus(prev => ({ ...prev, usda: 'error' }));
            return { type: 'usda' as const, data: [], error };
          }
        })(),
        
        // Open Food Facts API - with retry and timeout
        (async () => {
          try {
            if (signal?.aborted) return { type: 'off' as const, data: [], error: null };
            const response = await retryWithBackoff(
              () => {
                if (signal?.aborted) throw new Error('Aborted');
                return withTimeout(
                  supabase.functions.invoke('search-openfoodfacts', { body: { query } }),
                  8000
                );
              },
              2,
              500
            );
            if (signal?.aborted) return { type: 'off' as const, data: [], error: null };
            setApiStatus(prev => ({ ...prev, off: 'success' }));
            return { type: 'off' as const, data: response.data?.foods || [], error: response.error };
          } catch (error) {
            if (signal?.aborted) return { type: 'off' as const, data: [], error: null };
            console.error('[FoodSearch] OpenFoodFacts API error:', error);
            setApiStatus(prev => ({ ...prev, off: 'error' }));
            return { type: 'off' as const, data: [], error };
          }
        })()
      ];

      const results = await Promise.allSettled(searchPromises);
      
      // Check if aborted after searches complete
      if (signal?.aborted) {
        setIsSearching(false);
        return;
      }
      
      // Extract results from settled promises
      const basicResult = results[0].status === 'fulfilled' ? results[0].value : { type: 'basic' as const, data: [], error: null };
      const localResult = results[1].status === 'fulfilled' ? results[1].value : { type: 'local' as const, data: [], error: null };
      const usdaResult = results[2].status === 'fulfilled' ? results[2].value : { type: 'usda' as const, data: [], error: null };
      const offResult = results[3].status === 'fulfilled' ? results[3].value : { type: 'off' as const, data: [], error: null };

      console.log('[FoodSearch] Results:', {
        basic: basicResult.data?.length || 0,
        local: localResult.data?.length || 0,
        usda: usdaResult.data?.length || 0,
        off: offResult.data?.length || 0
      });

      // Check for errors and show appropriate messages
      // Only show error if ALL sources failed AND we have no results
      const allSourcesFailed = 
        (basicResult.error || basicResult.data.length === 0) &&
        (localResult.error || localResult.data.length === 0) &&
        (usdaResult.error || usdaResult.data.length === 0) &&
        (offResult.error || offResult.data.length === 0);
      
      const hasAnyResults = 
        (basicResult.data?.length || 0) > 0 ||
        (localResult.data?.length || 0) > 0 ||
        (usdaResult.data?.length || 0) > 0 ||
        (offResult.data?.length || 0) > 0;
      
      // Only show error if we truly have no results from any source
      // AND at least one external API explicitly failed (not just timeout/aborted)
      const externalApiErrors: string[] = [];
      if (usdaResult.error && !usdaResult.error.message?.includes('Aborted') && !usdaResult.error.message?.includes('timeout')) {
        externalApiErrors.push('USDA database');
      }
      if (offResult.error && !offResult.error.message?.includes('Aborted') && !offResult.error.message?.includes('timeout')) {
        externalApiErrors.push('Open Food Facts');
      }
      
      if (!hasAnyResults && externalApiErrors.length > 0) {
        // Only show error if we have no results AND external APIs had real errors (not timeouts)
        setSearchError(t('components.foodSearch.externalApiUnavailable', { apis: externalApiErrors.join(', ') }));
      } else {
        // Clear error if we have results or if it was just a timeout
        setSearchError(null);
      }
      
      // Log warnings for debugging but don't show to user if we have results
      if (usdaResult.error || offResult.error) {
        console.warn('[FoodSearch] Some APIs had issues:', {
          usda: usdaResult.error?.message,
          off: offResult.error?.message,
          hasResults: hasAnyResults
        });
      }

      // Track food search
      Analytics.foodSearched(query, {
        basic_results: basicResult.data?.length || 0,
        local_results: localResult.data?.length || 0,
        usda_results: usdaResult.data?.length || 0,
        off_results: offResult.data?.length || 0
      });

      const basicFoods = (basicResult.data || []).map((food: any) => ({
        ...food,
        verified: true,
        is_public: true,
        source: 'basic',
        brand: food.category
      }));

      const usdaResults = usdaResult.data || [];
      const offResults = offResult.data || [];
      
      // Add relevance scores to basic foods (boost by 30 for higher priority)
      const scoredBasicFoods = basicFoods.map((f: any) => ({
        ...f,
        source_type: 'basic',
        relevance_score: calculateLocalRelevance(f, query) + 30
      }));
      
      // Combine and sort all results by relevance score
      const allResults = [
        ...scoredBasicFoods,
        ...usdaResults.map((f: any) => ({ ...f, source_type: 'api' })),
        ...offResults.map((f: any) => ({ ...f, source_type: 'api' })),
        ...localResult.data.map(f => ({ ...f, source_type: 'local', relevance_score: calculateLocalRelevance(f, query) }))
      ];
      
      console.log('[FoodSearch] Total combined results:', allResults.length);
      
      // Sort by relevance score with priority: Basic > USDA > Local > OpenFoodFacts
      const sortedResults = allResults.sort((a, b) => {
        const scoreA = a.relevance_score || 0;
        const scoreB = b.relevance_score || 0;
        
        // If scores are equal, prioritize by source
        if (scoreA === scoreB) {
          if (a.source === 'basic') return -1;
          if (b.source === 'basic') return 1;
          if (a.source === 'usda') return -1;
          if (b.source === 'usda') return 1;
          if (a.source_type === 'local') return -1;
          if (b.source_type === 'local') return 1;
        }
        
        return scoreB - scoreA;
      }).slice(0, 25); // Limit to top 25 results
      
      console.log('[FoodSearch] Final results:', sortedResults.length);
      
      // Final abort check before setting results
      if (signal?.aborted) {
        setIsSearching(false);
        return;
      }
      
      setSearchResults(sortedResults);
      
      // Only show "no results" toast if we truly have no results and no error message is already showing
      if (sortedResults.length === 0 && !signal?.aborted && !searchError) {
        toast({
          title: t('components.foodSearch.noResultsTitle'),
          description: t('components.foodSearch.noResultsDescription', { query }),
          variant: "default"
        });
      }
    } catch (error: any) {
      // Don't show errors if search was aborted
      if (signal?.aborted) {
        setIsSearching(false);
        return;
      }
      
      console.error('[FoodSearch] Search error:', error);
      
      // Only show error if it's a critical failure, not a timeout or network hiccup
      const isCriticalError = error.message && 
        !error.message.includes('timeout') && 
        !error.message.includes('Aborted') &&
        !error.message.includes('network');
      
      if (isCriticalError) {
        setSearchError(t('components.foodSearch.searchFailedFallback'));
        toast({
          title: t('components.foodSearch.searchErrorTitle'),
          description: error.message || t('components.foodSearch.searchErrorGeneric'),
          variant: "destructive"
        });
      } else {
        // For timeouts/network issues, just clear error and try fallback
        setSearchError(null);
      }
      
      // Fallback to local search only (with abort check)
      try {
        if (signal?.aborted) {
          setIsSearching(false);
          return;
        }
        const results = await searchFoods(query);
        if (signal?.aborted) {
          setIsSearching(false);
          return;
        }
        if (results && results.length > 0) {
          setSearchResults(results);
          setApiStatus(prev => ({ ...prev, local: 'success' }));
          setSearchError(null); // Clear error if fallback found results
        } else {
          setSearchResults([]);
        }
      } catch (fallbackError) {
        if (signal?.aborted) {
          setIsSearching(false);
          return;
        }
        console.error('[FoodSearch] Fallback search also failed:', fallbackError);
        setSearchResults([]);
        // Only set error if fallback also fails and we have no results
        if (!searchError) {
          setSearchError(t('components.foodSearch.searchUnavailable'));
        }
      }
    } finally {
      // Always clear searching state to unblock input
      // Even if aborted, we want to allow the user to continue typing
      setIsSearching(false);
    }
  }, [searchFoods, toast]);

  const handleQuickAdd = async (food: any) => {
    try {
      const qty = food.lastQuantity || 1;
      const unit = food.lastUnit || 'serving';
      await onSelectFood(food, qty, unit);
      toast({
        title: t('components.foodSearch.foodAddedTitle'),
        description: mealType
          ? t('components.foodSearch.foodAddedToMeal', { name: food.name, meal: mealLabel })
          : t('components.foodSearch.foodAddedNoMeal', { name: food.name })
      });
    } catch (error) {
      console.error('Error adding food:', error);
    }
  };


  const handleCreateFood = async () => {
    try {
      const food = await createCustomFood(newFood);
      if (food) {
        Analytics.customFoodCreated(newFood.name);
        toast({
          title: t('components.foodSearch.customFoodCreatedTitle'),
          description: t('components.foodSearch.customFoodCreatedDescription', { name: newFood.name })
        });
        setShowCreateFood(false);
        setNewFood({
          name: '',
          serving_size: 100,
          serving_unit: 'g',
          calories_per_serving: 0,
          protein_per_serving: 0,
          carbs_per_serving: 0,
          fat_per_serving: 0,
          fiber_per_serving: 0,
          sugar_per_serving: 0,
          sodium_per_serving: 0
        });
      }
    } catch (error) {
      toast({
        title: t('components.foodSearch.errorTitle'),
        description: t('components.foodSearch.createFoodError'),
        variant: "destructive"
      });
    }
  };

  const handleAddFood = () => {
    if (selectedFood) {
      try {
        onSelectFood(selectedFood, quantity, unit);
        // Don't close the modal, just reset the selection to allow adding more foods
        setSelectedFood(null);
        setQuantity(1);
        setUnit('g');
        // Clear any search errors when food is successfully added
        setSearchError(null);
        toast({
          title: t('components.foodSearch.foodAddedTitle'),
          description: mealType
            ? t('components.foodSearch.foodAddedToMeal', { name: selectedFood.name, meal: mealLabel })
            : t('components.foodSearch.foodAddedNoMeal', { name: selectedFood.name })
        });
      } catch (error) {
        // Error handling is done in parent component (NutritionDashboard)
        console.error('[FoodSearchModal] Error in onSelectFood:', error);
      }
    }
  };

  useEffect(() => {
    // Abort any ongoing search immediately
    if (searchAbortControllerRef.current) {
      searchAbortControllerRef.current.abort();
      searchAbortControllerRef.current = null;
    }
    
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }

    // Always clear searching state when query changes to allow input editing
    setIsSearching(false);

    // Only search if query is at least 2 characters
    if (searchQuery.trim().length < 2) {
      setSearchResults([]);
      setSearchError(null);
      setApiStatus({ basic: 'idle', local: 'idle', usda: 'idle', off: 'idle' });
      return;
    }

    // Create new abort controller for this search
    const abortController = new AbortController();
    searchAbortControllerRef.current = abortController;

    // Increase debounce to 800ms to prevent too frequent searches and allow typing
    searchTimeoutRef.current = setTimeout(() => {
      // Double-check the abort controller is still valid before searching
      if (searchAbortControllerRef.current === abortController && !abortController.signal.aborted) {
        setIsSearching(true);
        handleSearch(searchQuery, abortController.signal);
      }
    }, 800);

    return () => {
      // Abort search when component unmounts or query changes
      if (searchAbortControllerRef.current) {
        searchAbortControllerRef.current.abort();
        searchAbortControllerRef.current = null;
      }
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = null;
      }
      // Always clear searching state on cleanup to unblock input
      setIsSearching(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]); // Removed handleSearch from deps to prevent unnecessary re-runs

  const allFoods = [...searchResults, ...userFoods.filter(uf => 
    uf.name.toLowerCase().includes(searchQuery.toLowerCase()))];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Apple className="h-5 w-5" />
            {mealType ? t('components.foodSearch.titleWithMeal', { meal: mealLabel }) : t('components.foodSearch.title')}
          </DialogTitle>
        </DialogHeader>

        {!showCreateFood ? (
          <Tabs defaultValue="search" className="flex-1 overflow-hidden flex flex-col">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="search">
                <Search className="h-4 w-4 mr-2" />
                {t('components.foodSearch.tabSearch')}
              </TabsTrigger>
              <TabsTrigger value="favorites">
                <Heart className="h-4 w-4 mr-2" />
                {t('components.foodSearch.tabFavorites')}
              </TabsTrigger>
              <TabsTrigger value="recent">
                <Clock className="h-4 w-4 mr-2" />
                {t('components.foodSearch.tabRecent')}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="search" className="flex-1 overflow-hidden flex flex-col space-y-4">
              {/* Search */}
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder={t('components.foodSearch.placeholder')}
                    value={searchQuery}
                    onChange={(e) => {
                      // Always allow input changes - never disable the input
                      setSearchQuery(e.target.value);
                    }}
                    className="pl-10 pr-10"
                    // Never disable input - user should always be able to type
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck="false"
                  />
                  {isSearching && searchQuery.trim().length >= 2 && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none z-10">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                    </div>
                  )}
                </div>
                <Button 
                  variant="outline" 
                  onClick={() => setShowCreateFood(true)}
                  className="shrink-0"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {t('components.foodSearch.createFood')}
                </Button>
              </div>

              {/* Error Message */}
              {searchError && (
                <div className="bg-destructive/10 border border-destructive/20 rounded-md p-3 text-sm text-destructive">
                  {searchError}
                </div>
              )}

              {/* API Status Indicators (for debugging) - only show when actively searching */}
              {isSearching && searchQuery.trim().length >= 2 && (
                <div className="flex gap-2 text-xs text-muted-foreground">
                  <span className={apiStatus.basic === 'loading' ? 'animate-pulse' : ''}>
                    {apiStatus.basic === 'loading' ? '🔄' : apiStatus.basic === 'success' ? '✅' : apiStatus.basic === 'error' ? '❌' : ''} Basic
                  </span>
                  <span className={apiStatus.local === 'loading' ? 'animate-pulse' : ''}>
                    {apiStatus.local === 'loading' ? '🔄' : apiStatus.local === 'success' ? '✅' : apiStatus.local === 'error' ? '❌' : ''} Local
                  </span>
                  <span className={apiStatus.usda === 'loading' ? 'animate-pulse' : ''}>
                    {apiStatus.usda === 'loading' ? '🔄' : apiStatus.usda === 'success' ? '✅' : apiStatus.usda === 'error' ? '❌' : ''} USDA
                  </span>
                  <span className={apiStatus.off === 'loading' ? 'animate-pulse' : ''}>
                    {apiStatus.off === 'loading' ? '🔄' : apiStatus.off === 'success' ? '✅' : apiStatus.off === 'error' ? '❌' : ''} OFF
                  </span>
                </div>
              )}

              {/* Food Selection */}
              {!selectedFood ? (
                <div className="flex-1 overflow-y-auto max-h-[400px]">
                  {isSearching ? (
                    <div className="text-center py-8 text-muted-foreground space-y-2">
                      <div className="flex items-center justify-center gap-2">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary"></div>
                        <span>{t('components.foodSearch.searching')}</span>
                      </div>
                      <p className="text-xs">{t('components.foodSearch.searchingHint')}</p>
                    </div>
                  ) : allFoods.length > 0 ? (
                    <div className="space-y-2">
                      {allFoods.map((food) => (
                        <Card
                          key={food.id} 
                          className="hover:bg-muted/50 transition-colors"
                        >
                          <CardContent className="p-4">
                            <div className="flex justify-between items-start gap-2">
                              <div 
                                className="flex-1 cursor-pointer"
                                onClick={() => setSelectedFood(food)}
                              >
                                <div className="flex items-center gap-2">
                                  <h3 className="font-medium">{food.name}</h3>
                                  {food.verified && (
                                    <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />
                                  )}
                                </div>
                                {'brand' in food && food.brand && (
                                  <p className="text-sm text-muted-foreground">{food.brand}</p>
                                )}
                                {'user_id' in food && (
                                  <p className="text-sm text-blue-600">{t('components.foodSearch.customFoodBadge')}</p>
                                )}
                                {food.verified && (
                                  <p className="text-xs text-green-600 mt-1">
                                    {
                                      (food as any).source === 'basic' ? t('components.foodSearch.verifiedBasic') :
                                      (food as any).source === 'usda' ? t('components.foodSearch.verifiedUsda') : 
                                      (food as any).source === 'openfoodfacts' ? t('components.foodSearch.verifiedOff') : 
                                      t('components.foodSearch.verifiedGeneric')
                                     }
                                   </p>
                                 )}
                               </div>
                               <div className="text-right text-sm shrink-0 ml-4 cursor-pointer" onClick={() => setSelectedFood(food)}>
                                 <p className="font-medium">{food.calories_per_serving} cal</p>
                                 <p className="text-muted-foreground">
                                   per {food.serving_size}{food.serving_unit}
                                 </p>
                               </div>
                               <Button
                                 variant="ghost"
                                 size="icon"
                                 className="shrink-0"
                                 onClick={(e) => {
                                   e.stopPropagation();
                                   toggleFavorite(food);
                                 }}
                               >
                                 <Heart 
                                   className={`h-5 w-5 ${isFavorite(food) ? 'fill-red-500 text-red-500' : 'text-muted-foreground'}`}
                                 />
                               </Button>
                             </div>
                            <div className="mt-2 text-xs text-muted-foreground grid grid-cols-3 gap-2">
                              <span>P: {food.protein_per_serving}g</span>
                              <span>C: {food.carbs_per_serving}g</span>
                              <span>F: {food.fat_per_serving}g</span>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  ) : searchQuery ? (
                    <div className="text-center py-8 text-muted-foreground">
                      {t('components.foodSearch.noFoodsForQuery', { query: searchQuery })}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      {t('components.foodSearch.startTyping')}
                    </div>
                  )}
                </div>
              ) : (
                /* Quantity Selection */
                <div className="space-y-4">
                  <Card>
                    <CardContent className="p-4">
                      <h3 className="font-medium">{selectedFood.name}</h3>
                      {'brand' in selectedFood && selectedFood.brand && (
                        <p className="text-sm text-muted-foreground">{selectedFood.brand}</p>
                      )}
                      <div className="mt-2 text-sm grid grid-cols-4 gap-2">
                        <span>Cal: {selectedFood.calories_per_serving}</span>
                        <span>P: {selectedFood.protein_per_serving}g</span>
                        <span>C: {selectedFood.carbs_per_serving}g</span>
                        <span>F: {selectedFood.fat_per_serving}g</span>
                      </div>
                    </CardContent>
                  </Card>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="quantity">{t('components.foodSearch.quantity')}</Label>
                      <Input
                        id="quantity"
                        type="number"
                        min="0.1"
                        step="0.1"
                        value={quantity}
                        onChange={(e) => setQuantity(parseFloat(e.target.value) || 1)}
                        onFocus={(e) => e.target.select()}
                      />
                    </div>
                    <div>
                      <Label htmlFor="unit">{t('components.foodSearch.unit')}</Label>
                      <select
                        id="unit"
                        value={unit}
                        onChange={(e) => setUnit(e.target.value)}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        <option value="g">{t('components.foodSearch.unitGrams')}</option>
                        <option value="serving">{t('components.foodSearch.unitServing')}</option>
                        <option value={selectedFood.serving_unit}>{selectedFood.serving_unit}</option>
                      </select>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setSelectedFood(null)} className="flex-1">
                      {t('components.foodSearch.back')}
                    </Button>
                    <Button onClick={handleAddFood} className="flex-1">
                      {mealType ? t('components.foodSearch.addToMeal', { meal: mealLabel }) : t('components.foodSearch.addFood')}
                    </Button>
                  </div>
                </div>
              )}
            </TabsContent>

            <TabsContent value="favorites" className="flex-1 overflow-y-auto space-y-4">
              {favoriteFoods.length > 0 ? (
                <div className="space-y-2">
                  {favoriteFoods.map((food) => (
                    <Card key={food.id} className="hover:bg-muted/50">
                      <CardContent className="p-3">
                        <div className="flex justify-between items-center gap-2">
                          <div 
                            className="flex-1 cursor-pointer"
                            onClick={() => setSelectedFood(food)}
                          >
                            <div className="flex items-center gap-2">
                              <h4 className="font-medium">{food.name}</h4>
                              {food.verified && (
                                <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />
                              )}
                            </div>
                            {food.brand && <p className="text-sm text-muted-foreground">{food.brand}</p>}
                            <p className="text-xs text-muted-foreground">
                              {food.calories_per_serving} cal per {food.serving_size}{food.serving_unit}
                            </p>
                            <div className="mt-1 text-xs text-muted-foreground grid grid-cols-3 gap-2">
                              <span>P: {food.protein_per_serving}g</span>
                              <span>C: {food.carbs_per_serving}g</span>
                              <span>F: {food.fat_per_serving}g</span>
                            </div>
                          </div>
                          <div className="flex gap-1 shrink-0">
                            <Button 
                              size="sm" 
                              onClick={() => handleQuickAdd(food)}
                            >
                              <Plus className="h-3 w-3 mr-1" />
                              {t('components.foodSearch.quickAdd')}
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleFavorite(food);
                              }}
                            >
                              <Heart className="h-5 w-5 fill-red-500 text-red-500" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-8">
                  {t('components.foodSearch.noFavorites')}
                </p>
              )}
            </TabsContent>

            <TabsContent value="recent" className="flex-1 overflow-y-auto space-y-4">
              {recentFoods.length > 0 ? (
                <div className="space-y-2">
                  {recentFoods.map((food, index) => (
                    <Card key={`${food.id}_${index}`} className="cursor-pointer hover:bg-muted/50">
                      <CardContent className="p-3">
                        <div className="flex justify-between items-center">
                          <div className="flex-1">
                            <h4 className="font-medium">{food.name}</h4>
                            {food.brand && <p className="text-sm text-muted-foreground">{food.brand}</p>}
                            <p className="text-xs text-muted-foreground">
                              {t('components.foodSearch.lastUsed', { quantity: food.lastQuantity || 1, unit: food.lastUnit || 'serving' })} • 
                              {food.calories_per_serving} {t('common.kcal')} per {food.serving_size}{food.serving_unit}
                            </p>
                          </div>
                          <Button 
                            size="sm" 
                            onClick={() => handleQuickAdd(food)}
                            className="ml-2"
                          >
                            <Plus className="h-3 w-3 mr-1" />
                            {t('components.foodSearch.quickAdd')}
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-8">
                  {t('components.foodSearch.noRecent')}
                </p>
              )}
            </TabsContent>
          </Tabs>
        ) : (
          /* Create Custom Food */
          <div className="flex-1 overflow-y-auto space-y-4">
            <h3 className="font-medium">{t('components.foodSearch.createFormTitle')}</h3>
            
            <div className="grid grid-cols-1 gap-4">
              <div>
                <Label htmlFor="foodName">{t('components.foodSearch.createNameRequired')}</Label>
                <Input
                  id="foodName"
                  value={newFood.name}
                  onChange={(e) => setNewFood({...newFood, name: e.target.value})}
                  placeholder={t('components.foodSearch.createNamePlaceholder')}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="servingSize">{t('components.foodSearch.createServingSizeRequired')}</Label>
                  <Input
                    id="servingSize"
                    type="number"
                    min="1"
                    value={newFood.serving_size}
                    onChange={(e) => setNewFood({...newFood, serving_size: parseFloat(e.target.value) || 100})}
                    onFocus={(e) => e.target.select()}
                  />
                </div>
                <div>
                  <Label htmlFor="servingUnit">{t('components.foodSearch.unit')}</Label>
                  <select
                    id="servingUnit"
                    value={newFood.serving_unit}
                    onChange={(e) => setNewFood({...newFood, serving_unit: e.target.value})}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <option value="g">{t('components.foodSearch.unitGramsLong')}</option>
                    <option value="oz">{t('components.foodSearch.unitOzLong')}</option>
                    <option value="ml">{t('components.foodSearch.unitMlLong')}</option>
                    <option value="cup">{t('components.foodSearch.unitCup')}</option>
                    <option value="piece">{t('components.foodSearch.unitPiece')}</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="calories">{t('components.foodSearch.createCaloriesRequired')}</Label>
                  <Input
                    id="calories"
                    type="number"
                    min="0"
                    value={newFood.calories_per_serving}
                    onChange={(e) => setNewFood({...newFood, calories_per_serving: parseFloat(e.target.value) || 0})}
                    onFocus={(e) => e.target.select()}
                  />
                </div>
                <div>
                  <Label htmlFor="protein">{t('components.foodSearch.createProteinLabel')}</Label>
                  <Input
                    id="protein"
                    type="number"
                    min="0"
                    step="0.1"
                    value={newFood.protein_per_serving}
                    onChange={(e) => setNewFood({...newFood, protein_per_serving: parseFloat(e.target.value) || 0})}
                    onFocus={(e) => e.target.select()}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="carbs">{t('components.foodSearch.createCarbsLabel')}</Label>
                  <Input
                    id="carbs"
                    type="number"
                    min="0"
                    step="0.1"
                    value={newFood.carbs_per_serving}
                    onChange={(e) => setNewFood({...newFood, carbs_per_serving: parseFloat(e.target.value) || 0})}
                    onFocus={(e) => e.target.select()}
                  />
                </div>
                <div>
                  <Label htmlFor="fat">{t('components.foodSearch.createFatLabel')}</Label>
                  <Input
                    id="fat"
                    type="number"
                    min="0"
                    step="0.1"
                    value={newFood.fat_per_serving}
                    onChange={(e) => setNewFood({...newFood, fat_per_serving: parseFloat(e.target.value) || 0})}
                    onFocus={(e) => e.target.select()}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="fiber">{t('components.foodSearch.createFiberLabel')}</Label>
                  <Input
                    id="fiber"
                    type="number"
                    min="0"
                    step="0.1"
                    value={newFood.fiber_per_serving}
                    onChange={(e) => setNewFood({...newFood, fiber_per_serving: parseFloat(e.target.value) || 0})}
                    onFocus={(e) => e.target.select()}
                  />
                </div>
                <div>
                  <Label htmlFor="sugar">{t('components.foodSearch.createSugarLabel')}</Label>
                  <Input
                    id="sugar"
                    type="number"
                    min="0"
                    step="0.1"
                    value={newFood.sugar_per_serving}
                    onChange={(e) => setNewFood({...newFood, sugar_per_serving: parseFloat(e.target.value) || 0})}
                    onFocus={(e) => e.target.select()}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="sodium">{t('components.foodSearch.createSodiumLabel')}</Label>
                <Input
                  id="sodium"
                  type="number"
                  min="0"
                  step="1"
                  value={newFood.sodium_per_serving}
                  onChange={(e) => setNewFood({...newFood, sodium_per_serving: parseFloat(e.target.value) || 0})}
                  onFocus={(e) => e.target.select()}
                  placeholder={t('components.foodSearch.createSodiumPlaceholder')}
                />
              </div>
            </div>

            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowCreateFood(false)} className="flex-1">
                {t('common.cancel')}
              </Button>
              <Button 
                onClick={handleCreateFood} 
                className="flex-1"
                disabled={!newFood.name || !newFood.calories_per_serving}
              >
                {t('components.foodSearch.createFoodCta')}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
      
    </Dialog>
  );
};